<!-- eslint-disable vue/no-mutating-props -->
<script setup lang="ts">
import type { SiteBlock } from '../../../types/site'

const props = defineProps<{ block: SiteBlock }>()

const t = useSiteEditorT()

const TARGETS = [
  { value: 'blank', label: t('site.f.contact_form.opt_new_tab', 'New tab') },
  { value: 'self', label: t('site.f.contact_form.opt_same_tab', 'Same tab') }
] as const

const FIELD_TYPES = [
  { label: t('site.f.contact_form.opt_single_line', 'Single line'), value: 'text' },
  { label: t('site.f.contact_form.opt_email', 'Email'), value: 'email' },
  { label: t('site.f.contact_form.opt_phone', 'Phone'), value: 'tel' },
  { label: t('site.f.contact_form.opt_paragraph', 'Paragraph'), value: 'textarea' },
  { label: t('site.f.contact_form.opt_dropdown', 'Dropdown'), value: 'select' },
  { label: t('site.f.contact_form.opt_checkbox', 'Checkbox'), value: 'checkbox' }
]

const addField = () => {
  props.block.data.fields = [
    ...(props.block.data.fields ?? []),
    { name: '', label: '', type: 'text' as const, required: false }
  ]
}
const removeField = (i: number) => {
  props.block.data.fields?.splice(i, 1)
}

const addHidden = () => {
  props.block.data.hidden_fields = [...(props.block.data.hidden_fields ?? []), { name: '', value: '' }]
}
const removeHidden = (i: number) => {
  props.block.data.hidden_fields?.splice(i, 1)
}
</script>

<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <UFormField :label="t('site.f.common.heading_optional', 'Heading (optional)')">
    <UInput
      v-model="block.data.headline"
      class="w-full"
    />
  </UFormField>
  <UFormField :label="t('site.f.common.intro_optional', 'Intro (optional)')">
    <UTextarea
      v-model="block.data.subtext"
      :rows="2"
      class="w-full"
    />
  </UFormField>

  <UFormField
    :label="t('site.f.common.submit_url_label', 'Where submissions go')"
    :help="t('site.f.contact_form.names_email_addresses_and_messages_are_s_help', 'Names, email addresses and messages are sent directly to this URL. They are not stored on this platform and we cannot see or recover them — you are responsible for the service you point this at, and your privacy policy should name it. Must start with https://.')"
    :error="!block.data.action_url ? t('site.f.contact_form.err_action_url', 'This block needs a destination URL before the page can be saved.') : undefined"
  >
    <UInput
      v-model="block.data.action_url"
      :placeholder="t('site.f.common.formspree_placeholder', 'https://formspree.io/f/...')"
      class="w-full"
    />
  </UFormField>

  <SiteEditorArrayItemCard
    v-for="(field, i) in block.data.fields ?? []"
    :key="i"
    :label="`${t('site.f.contact_form.item_label', 'Field')} ${i + 1}`"
    @remove="removeField(i)"
  >
    <UInput
      v-model="field.label"
      :placeholder="t('site.f.common.field_label_placeholder', 'Label shown to visitors')"
      class="w-full"
    />
    <UInput
      v-model="field.name"
      :placeholder="t('site.f.contact_form.field_name_sent_to_the_provider_e_g_emai', 'Field name sent to the provider, e.g. email')"
      class="w-full"
    />
    <USelect
      v-model="field.type"
      :items="FIELD_TYPES"
      class="w-full"
    />
    <UInput
      v-if="field.type !== 'checkbox' && field.type !== 'select'"
      :model-value="field.placeholder ?? ''"
      :placeholder="t('site.f.common.placeholder_field_label', 'Placeholder (optional)')"
      class="w-full"
      @update:model-value="field.placeholder = String($event)"
    />
    <UTextarea
      v-if="field.type === 'select'"
      :model-value="(field.options ?? []).join('\n')"
      :rows="3"
      :placeholder="t('site.f.common.options_per_line_placeholder', 'One option per line')"
      class="w-full"
      @update:model-value="field.options = String($event).split('\n').map(o => o.trim()).filter(Boolean)"
    />
    <div class="flex items-center justify-between gap-2 py-1">
      <span class="text-xs text-muted">{{ t('site.f.common.required_label', 'Required') }}</span>
      <USwitch v-model="field.required" />
    </div>
  </SiteEditorArrayItemCard>

  <UButton
    size="sm"
    variant="outline"
    color="neutral"
    icon="i-lucide-plus"
    @click="addField"
  >
    {{ t('site.f.contact_form.add_item', 'Add field') }}
  </UButton>

  <UFormField :label="t('site.f.common.button_text', 'Button text')">
    <UInput
      :model-value="block.data.button_text ?? ''"
      :placeholder="t('site.f.common.send_placeholder', 'Send')"
      class="w-full"
      @update:model-value="block.data.button_text = String($event)"
    />
  </UFormField>

  <SiteEditorFieldGroup
    :label="t('site.f.common.advanced_label', 'Advanced')"
    :default-open="!!(block.data.hidden_fields?.length)"
  >
    <UFormField
      :label="t('site.f.common.after_submit_label', 'After submitting, open')"
      :help="t('site.f.common.open_target_help', 'Choose Same tab if your provider redirects back to your site.')"
    >
      <SiteEditorOptionToggle
        :options="TARGETS"
        :model-value="block.data.target ?? 'blank'"
        @update:model-value="block.data.target = $event as 'self' | 'blank'"
      />
    </UFormField>

    <UFormField
      :label="t('site.f.common.confirmation_message_label', 'Confirmation message')"
      :help="t('site.f.common.confirmation_help', 'Shown after the visitor submits. We cannot tell whether the provider accepted it, so keep this neutral.')"
    >
      <UInput
        :model-value="block.data.success_text ?? ''"
        :placeholder="t('site.f.common.thanks_placeholder', 'Thanks — we\'ll be in touch.')"
        class="w-full"
        @update:model-value="block.data.success_text = String($event)"
      />
    </UFormField>

    <UFormField
      :label="t('site.f.common.consent_label', 'Consent checkbox (optional)')"
      :help="t('site.f.common.consent_help', 'When set, the visitor must tick this before submitting.')"
    >
      <UTextarea
        :model-value="block.data.consent_text ?? ''"
        :rows="2"
        :placeholder="t('site.f.common.privacy_consent_placeholder', 'I accept the privacy policy.')"
        class="w-full"
        @update:model-value="block.data.consent_text = String($event)"
      />
    </UFormField>

    <UFormField
      :label="t('site.f.common.hidden_fields_label', 'Hidden fields')"
      :help="t('site.f.contact_form.extra_values_some_providers_require_e_g_help', 'Extra values some providers require, e.g. Formspree\'s _subject and _next.')"
    >
      <div class="space-y-2">
        <div
          v-for="(hidden, i) in block.data.hidden_fields ?? []"
          :key="i"
          class="flex items-center gap-1.5"
        >
          <UInput
            v-model="hidden.name"
            :placeholder="t('site.f.common.name_placeholder', 'name')"
            class="w-full"
          />
          <UInput
            :model-value="hidden.value ?? ''"
            :placeholder="t('site.f.common.value_placeholder', 'value')"
            class="w-full"
            @update:model-value="hidden.value = String($event)"
          />
          <UButton
            size="xs"
            variant="ghost"
            color="error"
            icon="i-lucide-x"
            @click="removeHidden(i)"
          />
        </div>
        <UButton
          size="xs"
          variant="outline"
          color="neutral"
          icon="i-lucide-plus"
          @click="addHidden"
        >
          {{ t('site.f.common.add_hidden_field', 'Add hidden field') }}
        </UButton>
      </div>
    </UFormField>
  </SiteEditorFieldGroup>
</template>
